/**
 * OSRM-based parent→child route fetcher.
 *
 * The Project OSRM public demo (`router.project-osrm.org`) returns a
 * GeoJSON LineString of the actual road-network route between two
 * coordinates. Free, no API key, fair-use only — fine for dev / low-volume
 * production until we self-host. Callers MUST handle a `null` return
 * gracefully (rate-limit, network error, no route possible) so the UI
 * falls back to the straight-line track without breaking.
 *
 * If/when we self-host: keep the same `fetchRoute` signature and just
 * swap the base URL — no callers need to change.
 */

// Self-hosted OSRM (Armenia road graph) behind the APISIX gateway. The
// route /v1/route/* strips the prefix and forwards to osrm:5000, which
// exposes the OSRM HTTP API exactly as the public demo does — same
// /route/v1/{profile}/{coords} path, same response shape. Unlimited
// requests within the imported region.
const OSRM_BASE = `${import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io'}/v1/route`;
// driving | walking | cycling. For Parent Panic the parent is almost
// certainly driving toward the child; if we later expose a "Safe Walk"
// version we'll parameterize this.
const PROFILE = 'driving';
// Hard timeout — the panic flow can't wait forever for a route. 6s lets a
// slow connection succeed without making the UI feel hung.
const TIMEOUT_MS = 6_000;

export interface Route {
  /** [lng, lat] tuples following the road network. */
  coords: [number, number][];
  distanceMeters: number;
  durationSeconds: number;
}

interface OsrmResponse {
  code?: string;
  routes?: Array<{
    geometry: { type: 'LineString'; coordinates: [number, number][] };
    distance: number;
    duration: number;
  }>;
}

export async function fetchRoute(
  from: [number, number],
  to: [number, number],
): Promise<Route | null> {
  const url =
    `${OSRM_BASE}/route/v1/${PROFILE}/` +
    `${from[0]},${from[1]};${to[0]},${to[1]}` +
    `?overview=full&geometries=geojson&alternatives=false&steps=false`;
  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) return null;
    const data = (await res.json()) as OsrmResponse;
    const r = data.routes?.[0];
    if (data.code !== 'Ok' || !r) return null;
    return {
      coords: r.geometry.coordinates,
      distanceMeters: r.distance,
      durationSeconds: r.duration,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10_000 ? 1 : 0)} km`;
}

export function formatDuration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} h` : `${h} h ${rem} min`;
}
